import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react'
import type { Block, Transaction } from '@botho/core'
import type { ExplorerDataSource, ExplorerTab, ExplorerView, ExplorerContextValue } from './types'
import type { ClusterWealthEntry } from './wealth'

const ExplorerContext = createContext<ExplorerContextValue | null>(null)

export interface ExplorerProviderProps {
  /** Data source for fetching blocks and transactions */
  dataSource: ExplorerDataSource
  /** Whether the data source is ready (e.g., connected) */
  isReady?: boolean
  /** Initial search query (e.g., from URL parameter) */
  initialQuery?: string
  /** Callback when view changes (for URL sync) */
  onViewChange?: (view: ExplorerView) => void
  /** Children */
  children: ReactNode
}

export function ExplorerProvider({ dataSource, isReady = true, initialQuery, onViewChange, children }: ExplorerProviderProps) {
  const [view, setViewInternal] = useState<ExplorerView>({ mode: 'list' })
  const [blocks, setBlocks] = useState<Block[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState(initialQuery ?? '')
  const [initialQueryProcessed, setInitialQueryProcessed] = useState(false)
  const [activeTab, setActiveTabInternal] = useState<ExplorerTab>('blocks')
  const [clusterWealth, setClusterWealth] = useState<ClusterWealthEntry[] | null>(null)
  const [wealthLoading, setWealthLoading] = useState(false)
  const [wealthError, setWealthError] = useState<string | null>(null)

  const wealthSupported = typeof dataSource.getAllClusterWealth === 'function'

  // Wrapper to notify parent of view changes
  const setView = useCallback((newView: ExplorerView) => {
    setViewInternal(newView)
    onViewChange?.(newView)
  }, [onViewChange])

  // Load recent blocks
  const refresh = useCallback(async () => {
    if (!isReady) return

    setLoading(true)
    setError(null)

    try {
      const recentBlocks = await dataSource.getRecentBlocks({ limit: 20 })
      setBlocks(recentBlocks)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load blocks')
    } finally {
      setLoading(false)
    }
  }, [dataSource, isReady])

  // Load more blocks (older blocks below the current list)
  const loadMore = useCallback(async () => {
    if (!isReady || blocks.length === 0 || loadingMore) return

    setLoadingMore(true)
    try {
      const lastBlock = blocks[blocks.length - 1]
      const oldestHeight = lastBlock.height - 1

      // No older blocks to load
      if (oldestHeight < 0) {
        setLoadingMore(false)
        return
      }

      // Calculate the range of older blocks to fetch
      // startHeight is the lowest height to include, fetch up to startHeight + limit - 1
      const limit = 20
      const startHeight = Math.max(0, oldestHeight - limit + 1)
      const actualLimit = oldestHeight - startHeight + 1

      const moreBlocks = await dataSource.getRecentBlocks({
        limit: actualLimit,
        startHeight,
      })

      // Merge with deduplication by hash
      setBlocks((prev) => {
        const existingHashes = new Set(prev.map(b => b.hash))
        const newBlocks = moreBlocks.filter(b => !existingHashes.has(b.hash))
        return [...prev, ...newBlocks]
      })
    } catch (err) {
      console.error('Failed to load more blocks:', err)
    } finally {
      setLoadingMore(false)
    }
  }, [dataSource, isReady, blocks, loadingMore])

  // Search by height or hash. Accepts an optional query override so callers
  // (e.g. pressing Enter immediately after typing) can pass the current input
  // value without racing the async searchQuery state update.
  const search = useCallback(async (queryOverride?: string) => {
    const raw = queryOverride ?? searchQuery
    if (!isReady || !raw.trim()) return

    setLoading(true)
    setError(null)

    const query = raw.trim()

    try {
      // Check if it's a hash (64 hex chars) FIRST. An all-numeric 64-char
      // value (e.g. an all-zeros hash) is a hash, not a block height, so the
      // hash check must take precedence over the numeric-height check below.
      if (/^[0-9a-fA-F]{64}$/.test(query)) {
        // Try as block hash first
        const block = await dataSource.getBlock(query)
        if (block) {
          setView({ mode: 'block', block })
        } else {
          // Try as transaction hash
          const tx = await dataSource.getTransaction(query)
          if (tx) {
            setView({ mode: 'transaction', transaction: tx })
          } else {
            setError('Block or transaction not found')
          }
        }
      }
      // Otherwise check if it's a block height (number)
      else if (/^\d+$/.test(query)) {
        const block = await dataSource.getBlock(parseInt(query, 10))
        if (block) {
          setView({ mode: 'block', block })
        } else {
          setError(`Block at height ${query} not found`)
        }
      } else {
        setError('Invalid search query. Enter a block height or hash.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Search failed')
    } finally {
      setLoading(false)
    }
  }, [dataSource, isReady, searchQuery])

  // View a specific block
  const viewBlock = useCallback(
    async (blockOrHeightOrHash: Block | number | string) => {
      if (!isReady) return

      setLoading(true)
      setError(null)

      try {
        let block: Block | null
        if (typeof blockOrHeightOrHash === 'object') {
          block = blockOrHeightOrHash
        } else {
          block = await dataSource.getBlock(blockOrHeightOrHash)
        }

        if (block) {
          setView({ mode: 'block', block })
        } else {
          setError('Block not found')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load block')
      } finally {
        setLoading(false)
      }
    },
    [dataSource, isReady]
  )

  // View a transaction
  const viewTransaction = useCallback((transaction: Transaction) => {
    setView({ mode: 'transaction', transaction })
    setError(null)
  }, [setView])

  // Fetch a transaction by hash and view it (block-detail per-tx links, #699)
  const viewTransactionByHash = useCallback(
    async (txHash: string) => {
      if (!isReady) return

      setLoading(true)
      setError(null)

      try {
        const tx = await dataSource.getTransaction(txHash)
        if (tx) {
          setView({ mode: 'transaction', transaction: tx })
        } else {
          setError('Transaction not found')
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load transaction')
      } finally {
        setLoading(false)
      }
    },
    [dataSource, isReady, setView]
  )

  // Refresh cluster-wealth data (wealth-distribution tab, #699)
  const refreshWealth = useCallback(async () => {
    if (!isReady || !dataSource.getAllClusterWealth) return

    setWealthLoading(true)
    setWealthError(null)

    try {
      const clusters = await dataSource.getAllClusterWealth()
      setClusterWealth(clusters)
    } catch (err) {
      setWealthError(err instanceof Error ? err.message : 'Failed to load cluster wealth')
    } finally {
      setWealthLoading(false)
    }
  }, [dataSource, isReady])

  // Switch the list-mode tab; always lands back on the list view
  const setActiveTab = useCallback(
    (tab: ExplorerTab) => {
      setActiveTabInternal(tab)
      setView({ mode: 'list' })
      setError(null)
    },
    [setView]
  )

  // Go back to list
  const goBack = useCallback(() => {
    setView({ mode: 'list' })
    setError(null)
  }, [setView])

  // Initial load
  useEffect(() => {
    if (isReady) {
      refresh()
    }
  }, [isReady, refresh])

  // Process initial query from URL parameter
  useEffect(() => {
    if (isReady && initialQuery && !initialQueryProcessed) {
      setInitialQueryProcessed(true)
      // Trigger search with the initial query
      search()
    }
  }, [isReady, initialQuery, initialQueryProcessed, search])

  // Lazily fetch cluster wealth the first time the wealth tab is opened
  useEffect(() => {
    if (activeTab === 'wealth' && isReady && clusterWealth === null && !wealthLoading) {
      refreshWealth()
    }
  }, [activeTab, isReady, clusterWealth, wealthLoading, refreshWealth])

  // Subscribe to new blocks
  useEffect(() => {
    if (!isReady || !dataSource.onNewBlock) return

    const unsubscribe = dataSource.onNewBlock((block) => {
      setBlocks((prev) => {
        // Add new block at the beginning, remove duplicates
        const filtered = prev.filter((b) => b.hash !== block.hash)
        return [block, ...filtered].slice(0, 50)
      })
    })

    return unsubscribe
  }, [dataSource, isReady])

  const value: ExplorerContextValue = {
    view,
    blocks,
    loading,
    loadingMore,
    error,
    searchQuery,
    setSearchQuery,
    search,
    viewBlock,
    viewTransaction,
    viewTransactionByHash,
    goBack,
    loadMore,
    refresh,
    activeTab,
    setActiveTab,
    clusterWealth,
    wealthLoading,
    wealthError,
    wealthSupported,
    refreshWealth,
  }

  return <ExplorerContext.Provider value={value}>{children}</ExplorerContext.Provider>
}

export function useExplorer(): ExplorerContextValue {
  const context = useContext(ExplorerContext)
  if (!context) {
    throw new Error('useExplorer must be used within an ExplorerProvider')
  }
  return context
}
